import React from "react";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { FileUpload } from "primereact/fileupload";
import classNames from "classnames";
import { BaseModal } from "../../../../components/Modal/BaseModal";
import { CheckCircle, Paperclip } from "lucide-react";

interface WhitelistModalProps {
    isOpen: boolean;
    onHide: () => void;
    reason: string;
    attachment: File | null;
    reasonError: string;
    attachmentError: string;
    isSubmitting: boolean;
    onReasonChange: (value: string) => void;
    onFileSelect: (event: any) => void;
    onConfirm: () => void;
    onCancel: () => void;
}

export const WhitelistModal: React.FC<WhitelistModalProps> = ({
    isOpen,
    onHide,
    reason,
    attachment,
    reasonError,
    attachmentError,
    isSubmitting,
    onReasonChange,
    onFileSelect,
    onConfirm,
    onCancel
}) => {
    const footer = (
        <>
            <Button
                label="Cancel"
                onClick={onCancel}
                className="text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                disabled={isSubmitting}
                text
            />
            <Button
                label="Confirm"
                onClick={onConfirm}
                loading={isSubmitting}
                disabled={isSubmitting}
                className="bg-gradient-to-r from-green-500 to-teal-500 border-0 text-white px-6 py-2 rounded-md font-medium transition-all duration-300 shadow-[0_2px_8px_rgba(40,167,69,0.2)] hover:shadow-[0_4px_12px_rgba(40,167,69,0.3)] hover:-translate-y-0.5 active:translate-y-0 disabled:bg-gray-500 disabled:shadow-none disabled:cursor-not-allowed disabled:opacity-65 [&.p-button-loading]:bg-gray-500"
            />
        </>
    );

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onHide}
            title="Whitelist Customer"
            icon={<CheckCircle size={24} />}
            iconColor="#28a745"
            footer={footer}
            width="40rem"
            closeOnBackdrop={!isSubmitting}
            closeOnEscape={!isSubmitting}
            showCloseButton={!isSubmitting}
            className="whitelist-modal"
        >
            <div className="flex flex-col gap-6">
                <div className="text-gray-500 text-sm leading-relaxed">
                    Provide a valid reason and upload supporting documentation.
                </div>

                <div className="flex flex-col gap-2">
                    <label htmlFor="whitelist-reason" className="text-sm font-medium text-gray-700">
                        Reason <span className="text-red-500 ml-1">*</span>
                    </label>
                    <InputTextarea
                        id="whitelist-reason"
                        value={reason}
                        onChange={(e) => onReasonChange(e.target.value)}
                        rows={5}
                        className={classNames(
                            "w-full p-3 border rounded-md text-sm font-sans resize-y transition-all duration-200",
                            "focus:outline-none focus:ring-2 placeholder:text-gray-400",
                            "disabled:bg-gray-100 disabled:cursor-not-allowed",
                            {
                                "border-red-500 focus:border-red-500 focus:ring-red-100": reasonError,
                                "border-gray-300 focus:border-indigo-500 focus:ring-indigo-100": !reasonError,
                            }
                        )}
                        placeholder="Enter reason for whitelisting..."
                        autoResize
                        disabled={isSubmitting}
                    />
                    {reasonError && (
                        <small className="block text-red-500 text-xs leading-snug">{reasonError}</small>
                    )}
                </div>

                <div className="flex flex-col gap-2">
                    <label htmlFor="whitelist-attachment" className="text-sm font-medium text-gray-700">
                        Attachment <span className="text-red-500 ml-1">*</span>
                    </label>
                    <p className="text-gray-500 text-xs m-0 leading-relaxed">
                        Accepts only <b className="font-semibold text-gray-700">.pdf</b>, <b className="font-semibold text-gray-700">.doc</b>, <b className="font-semibold text-gray-700">.docx</b>; max <b className="font-semibold text-gray-700">10 MB</b>.
                    </p>
                    <div className="mb-2">
                        <FileUpload
                            mode="basic"
                            name="attachment"
                            accept=".pdf,.doc,.docx"
                            maxFileSize={10485760}
                            customUpload
                            uploadHandler={onFileSelect}
                            onSelect={onFileSelect}
                            chooseLabel="Choose File"
                            className={classNames(
                                "w-full",
                                "[&_.p-button]:!w-full [&_.p-button]:!justify-center [&_.p-button]:!p-3 [&_.p-button]:!px-4",
                                "[&_.p-button]:!rounded-md [&_.p-button]:!border-2 [&_.p-button]:!border-dashed",
                                "[&_.p-button]:!bg-gray-50 [&_.p-button]:!text-gray-700 [&_.p-button]:!font-medium",
                                "[&_.p-button]:!transition-all [&_.p-button]:!duration-200",
                                "hover:[&_.p-button]:!border-indigo-500 hover:[&_.p-button]:!bg-indigo-50 hover:[&_.p-button]:!text-indigo-500",
                                "disabled:[&_.p-button]:!opacity-60 disabled:[&_.p-button]:!cursor-not-allowed",
                                {
                                    "[&_.p-button]:!border-red-500": attachmentError && !attachment,
                                    "[&_.p-button]:!border-gray-300": !(attachmentError && !attachment),
                                }
                            )}
                            disabled={isSubmitting}
                        />
                    </div>
                    {attachment && (
                        <div className="px-3 py-3 bg-green-100 border border-green-200 rounded-md text-green-800 text-sm flex items-center gap-2 mt-0">
                            <Paperclip size={16} />
                            <span>Selected: <strong className="font-semibold">{attachment.name}</strong></span>
                        </div>
                    )}
                    {attachmentError && !attachment && (
                        <small className="block text-red-500 text-xs leading-snug">{attachmentError}</small>
                    )}
                </div>
            </div>
        </BaseModal>
    );
};
